<script setup lang="ts">
import { organizationsStore } from "../stores/Utils/auth/Organizations";
import Modal from "../components/Reusables/Modal/Modal.vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import OrganizationsModal from "../components/navigation/OrganizationsModal.vue";
import TopNav from "../components/navigation/TopNav.vue";
import MainSidebar from "../components/navigation/MainSidebar.vue";
import ProjectSubSidebar from "../components/navigation/ProjectSubSidebar.vue";
import AgentSubSidebar from "../components/navigation/AgentSubSidebar.vue";
import { clearLocalStorage } from "../services/Utils/clearLocalStorage";
import Tooltip from "@/features/Tooltip/Tooltip.vue";
import TutorialPlayerDock from "@/features/Tutorials/TutorialPlayerDock.vue";
import { useFilters } from "@/stores/project/project.filters";
import { useTenantFeatureAccess } from "@/composables/useTenantFeatureAccess";
import { useIsDesktop } from "@/composables/useIsDesktop";
import ChatPanel from "@features/Chat/ChatPanel.vue";
import LoadingLogoSpinner from "@/components/Reusables/Loading/LoadingLogoSpinner.vue";
import { isSwitchingTenant } from "@/state/tenantSwitch";
import {
  isAgentSubSidebarRoute,
  isMainSidebarExpandedRoute,
  isOwnNavigationRoute,
  isProjectSubSidebarRoute,
} from "@/router/navigationLayout";
import { useSpeckleCopy } from "@features/SpeckleCopy";

// App-wide on purpose: a clone navigates away the instant it returns, and the Speckle geometry
// copy it queued outlives whatever page the user goes to next (RTLCA-5478). Mounted here
// rather than in App.vue so it only opens once Hasura is authenticated.
useSpeckleCopy();

const { canUseAiChat } = useTenantFeatureAccess();

const isDesktop = useIsDesktop();

const useOrganizationStore = organizationsStore();

const filterStore = useFilters();
const { fetchOwners } = filterStore;

const LCAdaIsShown = ref<boolean>(false);

const route = useRoute();
const routeName = computed(() => route.name as string | undefined);

// Which column each route asks for, and whether the main sidebar keeps its labels, live in
// router/navigationLayout.ts as pure functions so they can be tested — a route rename that silently
// stops a column appearing is otherwise invisible until someone notices it missing.
//
// There used to be a hand-over here: the create wizard's step rail gave its column to the project
// navigation once it reported COMPLETED, since the project existed by then. Creating is a dialog now,
// so there is no rail to hand anything over and the project's own routes are the only claim.
const showsProjectSubSidebar = computed(() => isProjectSubSidebarRoute(routeName.value));
const showsAgentSubSidebar = computed(() => isAgentSubSidebarRoute(routeName.value));
// A route on OWN_NAVIGATION_ROUTE_NAMES renders its own navigation over the whole content area, so the
// main sidebar goes away entirely rather than collapsing to the icon rail (RTLCA-5435) — the LCA report.
// Safe because such a route is required to carry a way back of its own: the report's rail has
// "Back to dashboard". MainSidebar hides via a prop rather than v-if, so it keeps its grid track — see
// the template.
const showsMainSidebar = computed(() => !isOwnNavigationRoute(routeName.value));
const mainSidebarExpanded = computed(() => isMainSidebarExpandedRoute(routeName.value));

// The active sub-navigation can be collapsed to an icon rail (56px) via its header chevron. One
// shared preference across the project/agent sub-navs, persisted (non-critical UI chrome).
const SUB_NAV_STORAGE_KEY = "gaiup-sub-sidebar-expanded";
const readSubNavExpanded = (): boolean => {
  try {
    const stored = localStorage.getItem(SUB_NAV_STORAGE_KEY);
    if (stored === null) return true;
    return JSON.parse(stored) === true;
  } catch {
    return true;
  }
};
const subNavExpanded = ref(readSubNavExpanded());
watch(subNavExpanded, (value) => {
  try {
    localStorage.setItem(SUB_NAV_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // intentional: localStorage may be unavailable (private mode, quota) — collapse state is non-critical
  }
});

// `subNavExpanded` is bound straight to both sub-navs. The LCA report used to be the one route that
// opened this column collapsed by default, behind a route-local override so widening it there could not
// overwrite the preference the other project pages read; it shows no column at all now (RTLCA-5435), so
// the override went with it.

onMounted(async () => {
  fetchOwners();
  checkVersionNumber();
});

const checkVersionNumber = () => {
  //Used to check the version number, if the version has been changed then clear the localstorage in order to pubulate it again.
  const currentVersion = "1.0.6"; //Changing the version number is only necessary if any models, of which are stored in localstorage has been changed!

  const storedVersionNumber = localStorage.getItem("version-number");

  //If version number doesnt exist or version number has changed
  if (!storedVersionNumber || storedVersionNumber !== currentVersion) {
    clearLocalStorage(!storedVersionNumber ? true : false);
    localStorage.setItem("version-number", currentVersion);
    location.reload();
  }

  if (localStorage.getItem("lcada-state") !== "hidden") {
    LCAdaIsShown.value = true;
  }
};
const handleToggleLCAda = (shown: boolean) => {
  LCAdaIsShown.value = shown;
};
</script>

<template>
  <div class="flex h-screen w-screen flex-col bg-slate-50">
    <Tooltip />
    <TutorialPlayerDock />
    <TopNav
      :l-c-ada-is-shown="LCAdaIsShown"
      @toggle-l-c-ada="handleToggleLCAda" />
    <div
      class="duration-normal ease-standard grid flex-1 overflow-hidden transition-[grid-template-columns] will-change-[grid-template-columns]"
      :style="{
        gridTemplateColumns: isDesktop
          ? [
              showsMainSidebar ? (mainSidebarExpanded ? '200px' : '56px') : '0px',
              showsProjectSubSidebar ? (subNavExpanded ? '220px' : '56px') : '0px',
              showsAgentSubSidebar ? (subNavExpanded ? '180px' : '56px') : '0px',
              'minmax(0, 1fr)',
              'auto',
            ].join(' ')
          : 'minmax(0, 1fr) auto',
      }">
      <template v-if="isDesktop">
        <!-- Always rendered, hidden via `isVisible`: the grid above declares one track per column in
             order, so dropping an element with v-if would shift every later sidebar — and `main` — one
             track to the left, onto a 0px one. `isVisible` collapses the sidebar's contents and takes
             its links out of the tab order instead (same pattern as the sub-sidebars below). -->
        <MainSidebar
          :is-visible="showsMainSidebar"
          :is-expanded="mainSidebarExpanded"
          :l-c-ada-is-shown="LCAdaIsShown"
          @toggle-l-c-ada="handleToggleLCAda" />
        <ProjectSubSidebar
          v-model="subNavExpanded"
          :is-visible="showsProjectSubSidebar" />

        <AgentSubSidebar
          v-model="subNavExpanded"
          :is-visible="showsAgentSubSidebar" />
      </template>
      <main
        id="main"
        class="flex min-w-0 flex-col overflow-hidden will-change-[width] contain-[layout_style]">
        <div
          v-if="isSwitchingTenant"
          class="gap-px-16 flex h-full flex-col items-center justify-center"
          role="status"
          aria-live="polite">
          <LoadingLogoSpinner />
          <span class="t-small text-type-muted">Switching organization…</span>
        </div>
        <router-view v-else></router-view>
      </main>
    </div>
    <ChatPanel v-if="canUseAiChat && !isSwitchingTenant" />
    <Modal
      v-if="!useOrganizationStore.getIsHidden()"
      size="w-1/3"
      heading="Organizations"
      @toggle-modal="useOrganizationStore.toggleIsHidden()">
      <OrganizationsModal />
    </Modal>
  </div>
</template>
